import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Layers, Trash2, Pencil, ArrowUp, ArrowDown, EyeOff, Eye, GripVertical, Plus, Check } from "lucide-react";


const ACCENTS = ["terracotta", "sage", "ink"];

const emptyForm = {
  slug: "", title: "", description: "", cover_url: "",
  accent: "terracotta", book_ids: [], is_published: true, sort_order: 0,
};


export default function CollectionsTab() {
  const [rows, setRows] = useState([]);
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null);       // slug being edited, or 'new'
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        api.get("/admin/collections"),
        api.get("/books?limit=200"),
      ]);
      setRows(c.data || []);
      setBooks(b.data || []);
    } catch (err) {
      console.error("Failed to load collections/books:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing("new");
    setForm({ ...emptyForm, sort_order: rows.length });
  };

  const startEdit = async (slug) => {
    try {
      // Fetch the full collection with book_ids ordered
      const { data } = await api.get(`/collections/${slug}`);
      const row = rows.find((r) => r.slug === slug) || {};
      setForm({
        slug: data.slug,
        title: data.title,
        description: data.description || "",
        cover_url: data.cover_url || "",
        accent: data.accent || "terracotta",
        book_ids: (data.books || []).map((b) => b.id),
        is_published: row.is_published !== false,
        sort_order: row.sort_order || 0,
      });
      setEditing(slug);
    } catch {
      toast.error("Could not open collection");
    }
  };

  const save = async () => {
    if (!form.slug || !form.title) {
      toast.error("Slug and title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing === "new") {
        await api.post("/admin/collections", payload);
        toast.success("Collection created");
      } else {
        await api.put(`/admin/collections/${editing}`, payload);
        toast.success("Collection saved");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete collection "${slug}"?`)) return;
    try {
      await api.delete(`/admin/collections/${slug}`);
      toast("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const reorder = async (slug, direction) => {
    const idx = rows.findIndex((r) => r.slug === slug);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= rows.length) return;
    const a = rows[idx];
    const b = rows[swapIdx];
    // Persist swapped sort_order values (via full-row update, so we need the full record).
    try {
      const [detA, detB] = await Promise.all([
        api.get(`/collections/${a.slug}`),
        api.get(`/collections/${b.slug}`),
      ]);
      await Promise.all([
        api.put(`/admin/collections/${a.slug}`, {
          slug: a.slug, title: detA.data.title, description: detA.data.description || "",
          cover_url: detA.data.cover_url || "", accent: detA.data.accent || "terracotta",
          book_ids: (detA.data.books || []).map((x) => x.id),
          is_published: a.is_published !== false,
          sort_order: swapIdx,
        }),
        api.put(`/admin/collections/${b.slug}`, {
          slug: b.slug, title: detB.data.title, description: detB.data.description || "",
          cover_url: detB.data.cover_url || "", accent: detB.data.accent || "terracotta",
          book_ids: (detB.data.books || []).map((x) => x.id),
          is_published: b.is_published !== false,
          sort_order: idx,
        }),
      ]);
      load();
    } catch {
      toast.error("Could not reorder");
    }
  };

  const togglePublish = async (row) => {
    try {
      const { data } = await api.get(`/collections/${row.slug}`);
      await api.put(`/admin/collections/${row.slug}`, {
        slug: row.slug, title: data.title, description: data.description || "",
        cover_url: data.cover_url || "", accent: data.accent || "terracotta",
        book_ids: (data.books || []).map((b) => b.id),
        is_published: !(row.is_published !== false),
        sort_order: row.sort_order || 0,
      });
      load();
    } catch (e) {
      // Fallback: if get fails because unpublished, still try to publish it directly
      toast.error("Could not toggle");
    }
  };

  const toggleBook = (bookId) => {
    setForm((cur) => ({
      ...cur,
      book_ids: cur.book_ids.includes(bookId)
        ? cur.book_ids.filter((b) => b !== bookId)
        : [...cur.book_ids, bookId],
    }));
  };

  const moveBook = (bookId, direction) => {
    setForm((cur) => {
      const idx = cur.book_ids.indexOf(bookId);
      if (idx < 0) return cur;
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= cur.book_ids.length) return cur;
      const next = [...cur.book_ids];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...cur, book_ids: next };
    });
  };

  if (loading) return <div className="text-muted_ink">Loading…</div>;

  if (editing) {
    const orderedBooks = form.book_ids
      .map((id) => books.find((b) => b.id === id))
      .filter(Boolean);
    const unpicked = books.filter((b) => !form.book_ids.includes(b.id));

    return (
      <div data-testid="collection-editor" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="flex items-center gap-2 font-serif text-2xl">
            <Layers className="w-5 h-5 text-terracotta" />
            {editing === "new" ? "New collection" : `Edit "${form.title || editing}"`}
          </div>

          <div>
            <label className="label-eyebrow block mb-1">Slug (URL)</label>
            <Input
              data-testid="collection-slug-input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
              disabled={editing !== "new"}
              className="rounded-none border-edge font-mono text-sm"
              placeholder="autumn-reading"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Title</label>
            <Input
              data-testid="collection-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="rounded-none border-edge"
              placeholder="Autumn reading"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Description</label>
            <Textarea
              data-testid="collection-desc-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="rounded-none border-edge"
              placeholder="Slow, contemplative books to keep you company as the light shortens."
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Cover image URL (optional)</label>
            <Input
              data-testid="collection-cover-input"
              value={form.cover_url}
              onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
              className="rounded-none border-edge"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Accent colour</label>
            <div className="flex gap-2">
              {ACCENTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`accent-${a}`}
                  onClick={() => setForm({ ...form, accent: a })}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                    form.accent === a
                      ? `bg-${a} text-paper border-${a}`
                      : "border-edge bg-white hover:bg-linen"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="col-published"
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              className="accent-terracotta"
              data-testid="collection-publish-toggle"
            />
            <label htmlFor="col-published" className="text-sm">Published (visible to readers)</label>
          </div>

          <div className="flex gap-2 border-t border-edge pt-4">
            <button
              data-testid="save-collection-btn"
              onClick={save}
              disabled={saving}
              className="btn-primary disabled:opacity-60"
            >
              <Check className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save collection"}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="text-xs uppercase tracking-widest px-4 py-2 text-muted_ink hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right column: book picker */}
        <div className="space-y-4">
          <div className="label-eyebrow">In this collection ({form.book_ids.length})</div>
          <div className="border border-edge bg-linen p-2 max-h-80 overflow-y-auto">
            {orderedBooks.length === 0 ? (
              <div className="text-muted_ink text-sm p-4">No books yet. Pick some below.</div>
            ) : orderedBooks.map((b, i) => (
              <div key={b.id} data-testid={`picked-${b.id}`} className="flex items-center gap-2 py-2 px-2 hover:bg-white text-sm">
                <GripVertical className="w-3.5 h-3.5 text-muted_ink" />
                <div className="flex-1 truncate">
                  <span className="font-serif">{b.title}</span>
                  <span className="text-muted_ink italic"> — {b.author}</span>
                </div>
                <button onClick={() => moveBook(b.id, "up")} disabled={i === 0} className="text-muted_ink hover:text-ink disabled:opacity-30" title="Move up">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => moveBook(b.id, "down")} disabled={i === orderedBooks.length - 1} className="text-muted_ink hover:text-ink disabled:opacity-30" title="Move down">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggleBook(b.id)} className="text-muted_ink hover:text-terracotta" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="label-eyebrow">Add books</div>
          <div className="border border-edge bg-white p-2 max-h-80 overflow-y-auto">
            {unpicked.map((b) => (
              <button
                key={b.id}
                data-testid={`add-book-${b.id}`}
                onClick={() => toggleBook(b.id)}
                className="w-full flex items-center gap-2 py-2 px-2 hover:bg-linen text-sm text-left"
              >
                <Plus className="w-3.5 h-3.5 text-terracotta" />
                <div className="flex-1 truncate">
                  <span className="font-serif">{b.title}</span>
                  <span className="text-muted_ink italic"> — {b.author}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Table view
  return (
    <div data-testid="collections-tab" className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Curated shelves</div>
          <h3 className="font-serif text-3xl mt-1">{rows.length} collection{rows.length === 1 ? "" : "s"}</h3>
        </div>
        <button data-testid="new-collection-btn" onClick={startNew} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> New collection
        </button>
      </div>

      <div className="border border-edge bg-white">
        <div className="grid grid-cols-12 px-4 py-3 label-eyebrow border-b border-edge">
          <div className="col-span-1">#</div>
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Books</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-muted_ink text-sm">No collections yet — click "New collection" to make one.</div>
        ) : rows.map((r, idx) => (
          <div key={r.slug} data-testid={`collection-row-${r.slug}`} className="grid grid-cols-12 px-4 py-3 border-b border-edge last:border-b-0 items-center text-sm">
            <div className="col-span-1 flex items-center gap-1">
              <button onClick={() => reorder(r.slug, "up")} disabled={idx === 0} className="text-muted_ink hover:text-ink disabled:opacity-30">
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => reorder(r.slug, "down")} disabled={idx === rows.length - 1} className="text-muted_ink hover:text-ink disabled:opacity-30">
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="col-span-5">
              <div className="font-serif text-base">{r.title}</div>
              <div className="text-[11px] text-muted_ink font-mono">/{r.slug}</div>
            </div>
            <div className="col-span-2">{r.book_count} book{r.book_count === 1 ? "" : "s"}</div>
            <div className="col-span-2">
              <button
                onClick={() => togglePublish(r)}
                data-testid={`toggle-publish-${r.slug}`}
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border ${
                  r.is_published !== false
                    ? "border-sage bg-sage/10 text-sage"
                    : "border-edge bg-linen text-muted_ink"
                }`}
              >
                {r.is_published !== false ? <><Eye className="w-3 h-3" /> Live</> : <><EyeOff className="w-3 h-3" /> Draft</>}
              </button>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button data-testid={`edit-collection-${r.slug}`} onClick={() => startEdit(r.slug)} className="text-muted_ink hover:text-ink" title="Edit">
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid={`delete-collection-${r.slug}`} onClick={() => remove(r.slug)} className="text-muted_ink hover:text-terracotta" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
